import React, { useContext, useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import BgColorContainer from '../../Layout/BgColorContainer';
import Formulario from '../Home/Formulario';
import CursorContext from '../../providers/Cursor';
import useReveal from '../../../hooks/useReveal';
import useParallax from '../../../hooks/useParallax';
import useCursorZone from '../../../hooks/useCursorZone';
import molecula from '../../../assets/home/molecula-7.webp';
import grainCrema from '../../../assets/FondoHero.webp';
import { useAjustes } from '../../../cms/ajustes';
import { ConAcento, elemento } from '../../../cms/textos';

/** Textos de "Páginas → Inicio" (Home.jsx); correo y agenda, de "Ajustes del sitio". */
function Contacto({ textos }) {
    const { agenda, correo } = useAjustes();
    // El correo viene de "Ajustes del sitio": no es un texto de esta página y no tiene elemento propio
    const canales = [
        { etiqueta: textos.etiquetaCorreo, valor: correo, href: `mailto:${correo}`, Etiqueta: elemento(textos, 'etiquetaCorreo', 'span'), Valor: 'span' },
        { etiqueta: textos.etiquetaAgenda, valor: textos.textoAgenda, href: agenda, externo: true, Etiqueta: elemento(textos, 'etiquetaAgenda', 'span'), Valor: elemento(textos, 'textoAgenda', 'span') },
    ];
    const sectionRef = useRef(null);
    const ref = useReveal();
    const moleculaRef = useParallax({ y: -30 });
    const cursorZone = useCursorZone('light');
    const Ceja = elemento(textos, 'ceja', 'p');
    const Titulo = elemento(textos, 'titular', 'h2');
    const Bajada = elemento(textos, 'bajada', 'p');
    const { selectEnter, selectExit } = useContext(CursorContext);

    // La tarjeta del formulario se abre con el scroll, como el panel de Clientes.
    // Termina en un inset negativo para no recortar la sombra de la tarjeta.
    useGSAP(() => {
        gsap.fromTo('.contacto-tarjeta',
            { clipPath: 'inset(10% 10% 10% 10% round 32px)' },
            {
                clipPath: 'inset(-10% -10% -10% -10% round 32px)',
                ease: 'none',
                scrollTrigger: { trigger: '.contacto-tarjeta', start: 'top bottom', end: 'top 30%', scrub: true },
            });
    }, { scope: sectionRef });

    return (
        <BgColorContainer>
            {/* overflow-clip (no hidden): hidden crea un contenedor de scroll y rompe el sticky */}
            <section ref={sectionRef} id="contacto" className="relative overflow-clip bg-cream py-28 text-ink md:py-40" {...cursorZone}>
                <img src={grainCrema} alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-70" />

                <div className="container relative grid grid-cols-1 gap-16 px-5 md:px-10 lg:grid-cols-12 lg:gap-10 2xl:px-0">
                    <div ref={ref} className="relative flex flex-col gap-6 lg:sticky lg:top-28 lg:col-span-5 lg:self-start">
                        <Ceja data-reveal className="flex items-center gap-4 text-xs uppercase tracking-[0.3em] text-ink/50 md:text-sm">
                            <span>04</span>
                            <span className="h-px w-10 bg-ink/30" />
                            <span>{textos.ceja}</span>
                        </Ceja>
                        <Titulo data-reveal className="text-5xl font-medium leading-[1] tracking-tight md:text-7xl">
                            <ConAcento texto={textos.titular} />
                        </Titulo>
                        <Bajada data-reveal className="max-w-md text-lg leading-relaxed text-ink/60">
                            {textos.bajada}
                        </Bajada>

                        {/* Canales directos como filas, con la misma flecha de la lista de Servicios */}
                        <ul data-reveal className="mt-4 border-t border-ink/15">
                            {canales.map(({ etiqueta, valor, href, externo, Etiqueta, Valor }) => (
                                <li key={href} className="border-b border-ink/15">
                                    <a
                                        href={href}
                                        {...(externo ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                                        onMouseEnter={selectEnter}
                                        onMouseLeave={selectExit}
                                        className="group flex items-center justify-between gap-6 py-6"
                                    >
                                        <span className="flex flex-col gap-1">
                                            <Etiqueta className="text-xs uppercase tracking-[0.25em] text-ink/50">{etiqueta}</Etiqueta>
                                            <Valor className="text-xl transition-transform duration-500 ease-expo group-hover:translate-x-2 md:text-2xl">{valor}</Valor>
                                        </span>
                                        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full ring-1 ring-inset ring-ink/20 transition-all duration-500 ease-expo group-hover:-rotate-45 group-hover:bg-ink group-hover:text-cream">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
                                                <path d="M5 12h14M13 6l6 6-6 6" />
                                            </svg>
                                        </span>
                                    </a>
                                </li>
                            ))}
                        </ul>

                        <div ref={moleculaRef} className="pointer-events-none mt-4 hidden w-[55%] lg:block">
                            <img src={molecula} alt="" loading="lazy" className="molecula-float w-full" />
                        </div>
                    </div>

                    <div className="contacto-tarjeta lg:col-span-7">
                        <Formulario />
                    </div>
                </div>
            </section>
        </BgColorContainer>
    );
}

export default Contacto;
